// Vue plugin for error handling
import router from '@/router'
import store from '@/state/store'

// Check if error is session-related
function isSessionError(error) {
  if (!error) return false;
  
  const errorMessage = (error.message || error.toString()).toLowerCase();
  const sessionKeywords = [
    'session has expired',
    'please signin again',
    'unauthorized',
    'please sign in again',
    'session expired',
    'authentication failed',
    'invalid session',
    'token expired'
  ];
  
  return sessionKeywords.some(keyword => errorMessage.includes(keyword));
}

const ErrorHandlerPlugin = {
  install(Vue) {
    // Add global method for handling errors
    Vue.prototype.$handleError = function(error, context = '') {
      console.error(`Error ${context}:`, error)
      
      // Check if this is a session expiry error
      if (isSessionError(error)) {
        // Show session expired modal instead of error boundary
        this.$showSessionExpired();
        return;
      }
      
      // Store error in Vuex store
      if (store) {
        try {
          store.dispatch('error/captureError', { 
            error: error, 
            errorInfo: context 
          });
        } catch (storeError) {
          console.warn('Error module not available in store:', storeError);
        }
      }
      
      // Navigate to global error page
      router.push({
        name: 'ErrorBoundary',
        query: {
          timestamp: Date.now()
        }
      }).catch(() => {
        // Fallback if routing fails
        this.$showFallbackError()
      })
    }

    // Add method to show session expired modal
    Vue.prototype.$showSessionExpired = function() {
      // Dispatch a global event that App.vue will catch
      if (this.$root) {
        this.$root.$emit('sessionExpired');
      } else {
        // Fallback - redirect to login with message
        this.$showError('Your session has expired. Please sign in again.', 'Session Expired');
        setTimeout(() => {
          router.push('/login');
        }, 2000);
      }
    }

    // Add global method for safe async operations
    Vue.prototype.$safeAsync = async function(asyncOperation, fallback = null) {
      try {
        return await asyncOperation()
      } catch (error) {
        this.$handleError(error, 'in async operation')
        return fallback
      }
    }

    // Add method to show fallback error when routing fails
    Vue.prototype.$showFallbackError = function() {
      const errorDiv = document.createElement('div');
      errorDiv.innerHTML = `
        <div style="
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.7); display: flex; justify-content: center;
          align-items: center; z-index: 9999; 
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          backdrop-filter: blur(4px);
        ">
          <div style="
            background: white; padding: 3rem 2.5rem; border-radius: 12px; text-align: center;
            max-width: 450px; margin: 1rem; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            border: 1px solid #e5e7eb;
          ">
            <div style="
              width: 64px; height: 64px; background: #fef2f2; border-radius: 50%;
              display: flex; align-items: center; justify-content: center; margin: 0 auto 1.5rem;
            ">
              <svg style="width: 32px; height: 32px; color: #dc2626;" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM13 17h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <h2 style="color: #1f2937; font-size: 1.5rem; font-weight: 600; margin-bottom: 0.75rem; line-height: 1.4;">
              Oops! Something went wrong
            </h2>
            <p style="color: #6b7280; font-size: 1rem; margin-bottom: 2rem; line-height: 1.5;">
              It's our fault, not yours. Please try refreshing the page.
            </p>
            <button onclick="window.location.reload()" style="
              background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; border: none; 
              padding: 0.875rem 2rem; border-radius: 8px; cursor: pointer; font-size: 1rem; font-weight: 500;
              transition: all 0.2s ease; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); min-width: 140px;
            " onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 12px -2px rgba(0, 0, 0, 0.15)'" 
               onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 6px -1px rgba(0, 0, 0, 0.1)'">
              <svg style="width: 16px; height: 16px; margin-right: 8px; display: inline-block; vertical-align: middle;" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
              </svg>
              Reload Page
            </button>
            <div style="margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid #e5e7eb;">
              <p style="color: #9ca3af; font-size: 0.875rem; margin: 0;">© Wiremit</p>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(errorDiv);
    }

    // Add global method for showing user-friendly errors
    Vue.prototype.$showError = function(message, title = 'Error') {
      if (this.$swal) {
        this.$swal({
          icon: 'error',
          title: title,
          text: message,
          confirmButtonText: 'OK'
        })
      } else {
        alert(`${title}: ${message}`)
      }
    }

    // Add global method for showing success messages
    Vue.prototype.$showSuccess = function(message, title = 'Success') {
      if (this.$swal) {
        this.$swal({
          icon: 'success',
          title: title,
          text: message,
          timer: 3000,
          showConfirmButton: false
        })
      } else {
        alert(`${title}: ${message}`)
      }
    }

    // Add error boundary component
    Vue.component('ErrorBoundary', {
      name: 'ErrorBoundary',
      props: {
        fallback: {
          type: String,
          default: 'An error occurred in this section'
        },
        showDetails: {
          type: Boolean,
          default: false
        }
      },
      data() {
        return {
          hasError: false,
          error: null
        }
      },
      errorCaptured(err, instance, info) {
        this.hasError = true
        this.error = err
        console.error('Error boundary caught:', err, info)
        
        // Emit event for parent components
        this.$emit('error', err, info)
        
        return false // Stop error propagation
      },
      render(h) {
        if (this.hasError) {
          return h('div', {
            class: 'error-boundary alert alert-danger',
            style: {
              margin: '1rem 0',
              padding: '1rem',
              borderRadius: '0.375rem',
              border: '1px solid #f5c6cb'
            }
          }, [
            h('div', {
              class: 'd-flex align-items-center'
            }, [
              h('i', {
                class: 'fas fa-exclamation-triangle mr-2',
                style: { fontSize: '1.5rem' }
              }),
              h('div', [
                h('strong', 'Something went wrong'),
                this.showDetails && this.error ? h('div', {
                  class: 'small mt-1',
                  style: { fontFamily: 'monospace' }
                }, this.error.message) : null
              ])
            ]),
            h('button', {
              class: 'btn btn-sm btn-outline-danger mt-2',
              on: {
                click: () => {
                  this.hasError = false
                  this.error = null
                }
              }
            }, 'Try Again')
          ])
        }
        
        return this.$slots.default
      }
    })
  }
}

export default ErrorHandlerPlugin
